import React, { useState } from 'react';
import axios from 'axios';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { getUserImageUrl, createApiUrlWithPrefix } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useUserCounts } from '../contexts/UserCountsContext';
import { useLowStock } from '../contexts/LowStockContext';
import { useTranslation } from 'react-i18next';
import { hasAnyPermissions } from '../utils/permissions';
import NoPermissions from './NoPermissions';
import {
  Users,
  CreditCard,
  Calendar,
  BarChart3,
  LogOut,
  Menu,
  X,
  Globe,
  UserCheck,
  Users2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  DollarSign,
  Building,
  Building2,
  Package,
  Key,
  UserPlus,
  Settings
} from 'lucide-react';
import PasswordChangeModal from './PasswordChangeModal';
import { hasPagePermission } from '../utils/permissions';

function Layout() {
  const { user, logout, loading, refreshUser } = useAuth();
  const [pendingSubscribers, setPendingSubscribers] = useState(0);

  // Fetch pending subscriber count on mount
  React.useEffect(() => {
    const fetchPending = async () => {
      try {
        const resp = await axios.get(createApiUrlWithPrefix('subscribers?status=0'));
        const count = resp.data.totalItems || (Array.isArray(resp.data.items) ? resp.data.items.length : 0);
        setPendingSubscribers(count);
        console.log('Fetched pending subscribers for sidebar:', count);
      } catch (e) {
        console.error('Failed to fetch pending subscribers for sidebar', e);
      }
    };
    fetchPending();
  }, []);

  // Force refresh when component mounts to catch any updates from mobile
  React.useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔄 Layout mounted - refreshing user data to catch mobile updates...');
      refreshUser();
    }, 2000); // Small delay to avoid race conditions

    return () => clearTimeout(timer);
  }, []); // Only on mount
  const { userCounts } = useUserCounts();
  const { hasLowStock } = useLowStock();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [usersDropdownOpen, setUsersDropdownOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_users_open');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [paymentsDropdownOpen, setPaymentsDropdownOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_payments_open');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [institutionalDropdownOpen, setInstitutionalDropdownOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_institutional_open');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const toggleUsersDropdown = () => {
    const nextState = !usersDropdownOpen;
    setUsersDropdownOpen(nextState);
    localStorage.setItem('sidebar_users_open', JSON.stringify(nextState));
  };

  const togglePaymentsDropdown = () => {
    const nextState = !paymentsDropdownOpen;
    setPaymentsDropdownOpen(nextState);
    localStorage.setItem('sidebar_payments_open', JSON.stringify(nextState));
  };

  const toggleInstitutionalDropdown = () => {
    const nextState = !institutionalDropdownOpen;
    setInstitutionalDropdownOpen(nextState);
    localStorage.setItem('sidebar_institutional_open', JSON.stringify(nextState));
  };

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const isRTL = i18n.language === 'ar';

  // Show loading state while user data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-neutral-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Personnel-only navigation (Admin/SuperAdmin) - Organized by workflow
  const allNavigation = [
    { name: t('navigation.reports'), href: '/', icon: BarChart3, permission: 'Access Reports' },
    { name: t('navigation.payments'), href: '/payments', icon: CreditCard, hasSubmenu: true, permission: 'Access Payments' },
    { name: t('navigation.sessions'), href: '/sessions', icon: Calendar, permission: 'Access Sessions' },
    { name: t('navigation.attendance'), href: '/attendance', icon: ClipboardCheck, permission: 'Access Attendance' },
  ];

  // Filter navigation based on user permissions
  const navigation = allNavigation.filter(item => {
    const hasPermission = !item.permission || hasPagePermission(user, item.href);
    console.log(`Navigation item ${item.name} (${item.href}): hasPermission = ${hasPermission}`);
    return hasPermission;
  });

  // Payment-specific navigation
  const allPaymentNavigation = [
    { name: t('navigation.riderPayments'), href: '/payments/riders', icon: CreditCard, permission: 'Access Rider Payments' },
    { name: t('navigation.ownerPayments'), href: '/payments/owners', icon: DollarSign, permission: 'Access Owner Payments' },
    { name: t('navigation.temporaryPayments'), href: '/temporary/payments', icon: Users, permission: 'Access Temporary Users Management' },
  ];

  // Filter payment navigation based on permissions
  const paymentNavigation = allPaymentNavigation.filter(item => {
    const hasPermission = !item.permission || hasPagePermission(user, item.href);
    console.log(`Payment item ${item.name} (${item.href}): hasPermission = ${hasPermission}`);
    return hasPermission;
  });

  // Institutional-specific navigation
  const allInstitutionalNavigation = [
    { name: t('navigation.personnel'), href: '/institutional/personnel', icon: Users, permission: 'Access Institutional Personnel' },
    { name: t('navigation.other'), href: '/institutional/other', icon: Building, permission: 'Access Institutional Other' },
    { name: t('navigation.incomes'), href: '/institutional/incomes', icon: DollarSign, permission: 'Access Miscellaneous Payments' },
    { name: t('navigation.inventory'), href: '/inventory', icon: Package, permission: 'Access Inventory Management' },
  ];

  // Filter institutional navigation based on permissions
  const institutionalNavigation = allInstitutionalNavigation.filter(item => {
    const hasPermission = !item.permission || hasPagePermission(user, item.href);
    console.log(`Institutional item ${item.name} (${item.href}): hasPermission = ${hasPermission}`);
    return hasPermission;
  });


  // User role-specific navigation (for detailed user management)
  const allUsersNavigation = [
    { name: t('navigation.subscribers'), href: '/subscribers', icon: UserPlus, permission: 'Access Subscribers Management', count: pendingSubscribers, badge: true },
    { name: t('navigation.personnel'), href: '/personnel', icon: Users2, permission: 'Access Personnel Management', count: userCounts.personnel },
    { name: t('navigation.coaches'), href: '/coaches', icon: UserCheck, permission: 'Access Coaches Management', count: userCounts.coaches },
    { name: t('navigation.riders'), href: '/riders', icon: Users, permission: 'Access Riders Management', count: userCounts.riders },
    { name: t('navigation.owners'), href: '/owners', icon: Users2, permission: 'Access Owners Management', count: userCounts.owners },
    { name: t('navigation.temporaryUsers'), href: '/temporary', icon: Users, permission: 'Access Temporary Users Management', count: userCounts.temporary },
  ];

  // Filter user navigation based on permissions
  const usersNavigation = allUsersNavigation.filter(item => {
    const hasPermission = !item.permission || hasPagePermission(user, item.href);
    console.log(`User item ${item.name} (${item.href}): hasPermission = ${hasPermission}`);
    return hasPermission;
  });

  const isSuperAdmin = user?.role === 4 || user?.role === 'SuperAdmin';

  // Debug logging
  console.log('=== LAYOUT DEBUG ===');
  console.log('Layout - User object:', user);
  console.log('Layout - User permissions:', user?.permissions);
  console.log('Layout - User role:', user?.role, typeof user?.role);
  console.log('Layout - User permissions array length:', user?.permissions?.length);
  console.log('Layout - User permissions names:', user?.permissions?.map(p => p.name));
  console.log('Layout - User permissions details:', user?.permissions);
  console.log('Layout - Final navigation arrays:');
  console.log('  - navigation:', navigation.length, 'items');
  console.log('  - usersNavigation:', usersNavigation.length, 'items');
  console.log('  - paymentNavigation:', paymentNavigation.length, 'items');
  console.log('  - institutionalNavigation:', institutionalNavigation.length, 'items');
  console.log('=== END LAYOUT DEBUG ===');

  // Check if user is null
  if (!user) {
    console.log('ERROR: User is null!');
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600">{t('common.noUserData')}</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            {t('common.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // Check if user has any permissions
  if (!hasAnyPermissions(user)) {
    console.log('User has no permissions, showing NoPermissions component');
    return <NoPermissions />;
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setShowLanguageMenu(false);
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  return (
    <div className="h-screen min-h-screen h-[100dvh] bg-neutral-50 flex overflow-hidden overscroll-behavior-y-contain" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-neutral-800 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 z-50 w-72 bg-white/95 backdrop-blur-xl border-r border-gray-100 shadow-[4px_0_24px_rgba(0,0,0,0.02)] transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:z-auto ${isRTL
        ? `right-0 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`
        : `left-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
        }`}>
        <div className="flex h-full flex-col">
          {/* Logo - Fixed at top */}
          <div className={`flex h-20 items-center justify-between px-6 border-b border-gray-50/50 flex-shrink-0 bg-gradient-to-b from-white to-gray-50/30 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center space-x-4">
              <div className="h-11 w-11 flex items-center justify-center rounded-xl bg-white shadow-md border border-gray-100 ring-1 ring-gray-50 transition-transform hover:scale-105 duration-300">
                <img
                  src="/logo.jpg"
                  alt={t('app.logoAlt')}
                  className="h-9 w-9 object-contain rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-50 to-secondary-50" style={{ display: 'none' }}>
                  <span className="text-xl">🐎</span>
                </div>
              </div>
              <div className="flex flex-col">
                <h1 className="text-lg font-bold text-gray-900 leading-tight tracking-tight">{t('app.clubName')}</h1>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mt-0.5">{t('app.clubLocation')}</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto min-h-0">
            {/* Reports - First item */}
            {hasPagePermission(user, '/') && (
              <Link
                to="/"
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${location.pathname === '/'
                  ? 'bg-primary-100 text-primary-900 shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                  } ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <BarChart3
                  className={`h-5 w-5 flex-shrink-0 ${location.pathname === '/' ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                    } ${isRTL ? 'ml-3' : 'mr-3'}`}
                />
                {t('navigation.reports')}
              </Link>
            )}

            {/* User Management Dropdown - Second item */}
            {usersNavigation.length > 0 && (
              <div className="space-y-1">
                <button
                  onClick={toggleUsersDropdown}
                  className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Users2 className={`h-5 w-5 flex-shrink-0 text-neutral-400 group-hover:text-neutral-500 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                    {t('navigation.users')}
                  </div>
                  {usersDropdownOpen ? (
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-neutral-400" />
                  )}
                </button>

                {usersDropdownOpen && (
                  <div className="ml-6 space-y-1">
                    {usersNavigation.map((item) => {
                      const isActive = location.pathname === item.href;
                      const IconComponent = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                            ? 'bg-primary-100 text-primary-900 shadow-sm'
                            : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                            } ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          <IconComponent
                            className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                              } ${isRTL ? 'ml-2' : 'mr-2'}`}
                          />
                          <span className="flex-1">{item.name}</span>
                          {!userCounts.loading && item.count !== undefined && item.count > 0 && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.badge && item.count > 0
                              ? 'bg-red-500 text-white animate-pulse'
                              : isActive
                                ? 'bg-primary-200 text-primary-800'
                                : 'bg-neutral-100 text-neutral-600'
                              }`}>
                              {item.count}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Payments Dropdown */}
            {paymentNavigation.length > 0 && (
              <div className="space-y-1">
                <button
                  onClick={togglePaymentsDropdown}
                  className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CreditCard className={`h-5 w-5 flex-shrink-0 text-neutral-400 group-hover:text-neutral-500 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                    {t('navigation.payments')}
                  </div>
                  {paymentsDropdownOpen ? (
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-neutral-400" />
                  )}
                </button>

                {paymentsDropdownOpen && (
                  <div className="ml-6 space-y-1">
                    {paymentNavigation.map((item) => {
                      if (item.submenu) {
                        // Handle submenu items
                        return (
                          <div key={item.name} className="space-y-1">
                            <div className="flex items-center px-3 py-2 text-sm font-medium text-neutral-500">
                              <item.icon className="h-4 w-4 mr-2" />
                              {item.name}
                            </div>
                            <div className="ml-6 space-y-1">
                              {item.submenu.map((subItem) => {
                                const isActive = location.pathname === subItem.href;
                                const IconComponent = subItem.icon;
                                return (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                                      ? 'bg-primary-100 text-primary-900 shadow-sm'
                                      : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                                      } ${isRTL ? 'flex-row-reverse' : ''}`}
                                  >
                                    <IconComponent
                                      className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                                        } ${isRTL ? 'ml-2' : 'mr-2'}`}
                                    />
                                    {subItem.name}
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        );
                      } else {
                        // Handle regular items
                        const isActive = location.pathname === item.href;
                        const IconComponent = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                              ? 'bg-primary-100 text-primary-900 shadow-sm'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                              } ${isRTL ? 'flex-row-reverse' : ''}`}
                          >
                            <IconComponent
                              className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                                } ${isRTL ? 'ml-2' : 'mr-2'}`}
                            />
                            {item.name}
                          </Link>
                        );
                      }
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Other Navigation Items - Sessions, Attendance, Permission Management */}
            {navigation.slice(2).map((item) => {
              const isActive = location.pathname === item.href;
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                    ? 'bg-primary-100 text-primary-900 shadow-sm'
                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                    } ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <IconComponent
                    className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                      } ${isRTL ? 'ml-3' : 'mr-3'}`}
                  />
                  {item.name}
                </Link>
              );
            })}

            {/* Settings - SuperAdmin only */}
            {isSuperAdmin && (
              <Link
                to="/admin/settings"
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${location.pathname === '/admin/settings'
                  ? 'bg-primary-100 text-primary-900 shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                  } ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <Settings
                  className={`h-5 w-5 flex-shrink-0 ${location.pathname === '/admin/settings' ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                    } ${isRTL ? 'ml-3' : 'mr-3'}`}
                />
                {t('navigation.settings')}
              </Link>
            )}

            {/* Institutional Dropdown */}
            {institutionalNavigation.length > 0 && (
              <div className="space-y-1">
                <button
                  onClick={toggleInstitutionalDropdown}
                  className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 relative ${isRTL ? 'flex-row-reverse' : ''} ${hasLowStock ? 'animate-pulse' : ''}`}
                >
                  <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="relative">
                      <Building className={`h-5 w-5 flex-shrink-0 text-neutral-400 group-hover:text-neutral-500 ${isRTL ? 'ml-3' : 'mr-3'} ${hasLowStock ? 'text-red-500' : ''}`} />
                      {hasLowStock && (
                        <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-ping"></span>
                      )}
                      {hasLowStock && (
                        <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
                      )}
                    </div>
                    <span className="flex items-center">
                      {t('navigation.institutional')}
                      {hasLowStock && (
                        <span className="ml-2 text-xs font-semibold text-red-600">
                          ⚠️
                        </span>
                      )}
                    </span>
                  </div>
                  {institutionalDropdownOpen ? (
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-neutral-400" />
                  )}
                </button>

                {institutionalDropdownOpen && (
                  <div className="ml-6 space-y-1">
                    {institutionalNavigation.map((item) => {
                      const isActive = location.pathname === item.href;
                      const IconComponent = item.icon;
                      const isInventory = item.href === '/inventory';
                      const showLowStockIndicator = isInventory && hasLowStock;

                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors relative ${isActive
                            ? 'bg-primary-100 text-primary-900 shadow-sm'
                            : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                            } ${isRTL ? 'flex-row-reverse' : ''} ${showLowStockIndicator ? 'animate-pulse' : ''}`}
                        >
                          <div className={`relative ${isRTL ? 'ml-2' : 'mr-2'}`}>
                            <IconComponent
                              className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'
                                } ${showLowStockIndicator ? 'text-red-500' : ''}`}
                            />
                            {showLowStockIndicator && (
                              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-ping"></span>
                            )}
                            {showLowStockIndicator && (
                              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
                            )}
                          </div>
                          <span className="flex items-center">
                            {item.name}
                            {showLowStockIndicator && (
                              <span className="ml-2 text-xs font-semibold text-red-600">
                                ⚠️
                              </span>
                            )}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* User info - Fixed at bottom */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0 bg-white">
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-600">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className={`flex-1 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                <p className="text-sm font-medium text-neutral-700">{user?.name}</p>
                <p className="text-xs text-neutral-500">{user?.role ? t(`roles.${String(user.role).toLowerCase()}`) : ''}</p>
              </div>
              <div className={`flex items-center space-x-1 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <button
                  onClick={() => setShowPasswordChange(true)}
                  className={`text-neutral-400 hover:text-neutral-600 p-1 rounded-md hover:bg-neutral-100`}
                  title={t('layout.changePassword')}
                >
                  <Key className="h-5 w-5" />
                </button>
                <button
                  onClick={handleLogout}
                  className={`text-neutral-400 hover:text-neutral-600 p-1 rounded-md hover:bg-neutral-100`}
                  title={t('navigation.logout')}
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-neutral-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => setSidebarOpen(true)}
                className={`lg:hidden text-neutral-400 hover:text-neutral-600 ${isRTL ? 'ml-4' : 'mr-4'}`}
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>

            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2 sm:space-x-4' : 'space-x-2 sm:space-x-4'}`}>
              {/* Language selector */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className={`flex items-center text-neutral-600 hover:text-neutral-900 p-2 rounded-md hover:bg-neutral-100 ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
                >
                  <Globe className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {languages.find(lang => lang.code === i18n.language)?.flag}
                  </span>
                </button>

                {showLanguageMenu && (
                  <div className={`absolute mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50 ${isRTL ? 'left-0' : 'right-0'}`}>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-2 text-sm hover:bg-neutral-100 flex items-center ${i18n.language === lang.code ? 'bg-primary-50 text-primary-700' : 'text-neutral-700'
                          } ${isRTL ? 'text-right space-x-reverse space-x-2' : 'text-left space-x-2'}`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>


              {/* User profile */}
              <div
                className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} cursor-pointer hover:opacity-80 transition-opacity`}
                onClick={() => {
                  console.log('🔄 Manual refresh triggered from profile click');
                  refreshUser();
                }}
                title="Click to refresh profile"
              >
                {getUserImageUrl(user) ? (
                  <img
                    src={getUserImageUrl(user)}
                    alt={user?.name || 'User'}
                    className="h-8 w-8 rounded-full object-cover border-2 border-primary-100"
                    onError={(e) => {
                      console.error('🖼️ Image load error:', e);
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                    onLoad={() => {
                      console.log('🖼️ Profile image loaded successfully:', getUserImageUrl(user));
                    }}
                    key={`img-${user?.id}-${user?.updatedAt || user?.UpdatedAt || ''}-${user?.photoPath || user?.PhotoPath || user?.imagePath || user?.ImagePath || ''}`} // Force re-render when image changes
                  />
                ) : null}
                <div
                  className={`h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center ${getUserImageUrl(user) ? 'hidden' : 'flex'}`}
                >
                  <span className="text-sm font-medium text-primary-600">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-neutral-700">{user?.name}</p>
                  <p className="text-xs text-neutral-500">{user?.role ? t(`roles.${user.role}`) : ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordChange}
        onClose={() => setShowPasswordChange(false)}
        onSuccess={() => {
          // Optionally show a success message or refresh user data
          console.log('Password changed successfully');
        }}
      />
    </div>
  );
}

export default Layout;
